// Service Steady PWA — src/components/WeatherCard.tsx — v3.0 — built 2026-06-04
//
// Compact gradient weather card. ~140px tall, single row of meaningful
// content, tap to open the detail modal. Town name button (with chevron)
// opens the location picker — tap propagation stops on that button so a
// town tap doesn't ALSO open the detail modal.
//
// v3 minimizes — no forecast pills, no wash window. All extended content
// lives in the detail modal. Server provides current.category +
// current.is_day; we map to a Tailwind gradient via getGradient().

import { useEffect, useState } from 'react';
import {
  ChevronDown,
  Cloud,
  CloudFog,
  CloudRain,
  CloudSnow,
  Droplets,
  RefreshCw,
  Sun,
  Umbrella,
  Zap,
} from 'lucide-react';
import { useWeather, useWeatherDetail } from '@/hooks/useWeather';
import { useLocation } from '@/hooks/useLocation';
import { getGradient, getTextOnGradient } from '@/lib/weatherStyle';
import { daypartsFromDetail, type DaypartPrecip } from '@/lib/weatherDayparts';
import { LocationPickerModal } from './LocationPickerModal';
import { WeatherDetailModal } from './WeatherDetailModal';
import type { WeatherCategory } from '@/lib/api/weather';

const PLACEHOLDER_NAME_RE = /^\s*(?:current location|)\s*$/i;

// ---------------------------------------------------------------------------
// Icon mapping
// ---------------------------------------------------------------------------

function IconFor({
  category,
  className,
}: {
  category: WeatherCategory;
  className?: string;
}) {
  const c = className ?? 'h-6 w-6';
  switch (category) {
    case 'clear':
      return <Sun className={c} />;
    case 'partly_cloudy':
      return <Cloud className={c} />;
    case 'cloudy':
      return <Cloud className={c} />;
    case 'rain':
      return <CloudRain className={c} />;
    case 'snow':
      return <CloudSnow className={c} />;
    case 'thunder':
      return <Zap className={c} />;
    case 'fog':
      return <CloudFog className={c} />;
    default:
      return <Cloud className={c} />;
  }
}

// ---------------------------------------------------------------------------
// Skeleton state (loading)
// ---------------------------------------------------------------------------

function SkeletonCard() {
  return (
    <div className="px-5 pb-1 pt-3">
      <div className="relative h-[160px] overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-slate-400 via-slate-500 to-slate-600 p-4 text-white shadow-lg">
        <div className="h-4 w-32 animate-pulse rounded bg-white/20" />
        <div className="mt-3 h-10 w-24 animate-pulse rounded bg-white/20" />
        <div className="mt-2 h-3 w-40 animate-pulse rounded bg-white/20" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error state (still gradient-backed for visual consistency)
// ---------------------------------------------------------------------------

function ErrorCard({
  onRefresh,
}: {
  onRefresh: () => void;
}) {
  return (
    <div className="px-5 pb-1 pt-3">
      <div className="relative h-[160px] overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-slate-500 via-slate-600 to-slate-700 p-4 text-white shadow-lg">
        <div className="flex h-full items-center justify-between">
          <div className="text-sm opacity-90">Weather unavailable</div>
          <button
            type="button"
            onClick={onRefresh}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 backdrop-blur-sm hover:bg-white/25"
            aria-label="Refresh weather"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main card
// ---------------------------------------------------------------------------

export function WeatherCard() {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const { data, isLoading, isFetching, isError, refetch } = useWeather();
  // The card payload has no hourly data, so read the detail payload here too
  // (PWA-only — no Worker change) to derive the 4-daypart precip glance. Same
  // query key as the modal → a single shared fetch that ALSO pre-warms the
  // modal so it opens instantly. The card renders fine while this loads/errors
  // (the daypart row just doesn't show). 2026-06-05.
  const { data: detailData } = useWeatherDetail(true);
  const { location, setLocation } = useLocation();

  // 2026-06-04 (v3.2) — persist the resolved name. When the user taps
  // "Use my location", LocationPickerModal seeds the location with the
  // placeholder "Current Location" and the Worker reverse-geocodes via NWS
  // to return "City, ST" in the response. We persist that real name back
  // to localStorage so future reloads display the town instantly without
  // re-asking for geolocation permission.
  useEffect(() => {
    if (!data || !data.ok) return;
    const apiName = data.location?.name?.trim();
    if (!apiName) return;
    if (!PLACEHOLDER_NAME_RE.test(location.name)) return;
    if (apiName === location.name) return;
    setLocation({
      name: apiName,
      lat: location.lat,
      lon: location.lon,
    });
  }, [data, location.name, location.lat, location.lon, setLocation]);

  if (isLoading) return <SkeletonCard />;
  if (isError || !data || !data.ok) {
    return (
      <>
        <ErrorCard onRefresh={() => void refetch()} />
        <LocationPickerModal open={pickerOpen} onClose={() => setPickerOpen(false)} />
      </>
    );
  }

  const { location: apiLocation, current, today, nowcast } = data;
  const detailOk = detailData && detailData.ok ? detailData : null;
  // Card shows current + upcoming dayparts only (elapsed blocks dropped) — the
  // "what's left today / tonight" planning glance. All four live in the modal.
  const cardDayparts: DaypartPrecip[] = detailOk
    ? daypartsFromDetail(detailOk).filter((dp) => !dp.isElapsed)
    : [];
  const gradient = getGradient(current.category, current.is_day);
  const textColor = getTextOnGradient(current.category, current.is_day);
  const hi = today.high_f !== null ? `${today.high_f}°` : '—';
  const lo = today.low_f !== null ? `${today.low_f}°` : '—';
  // Use CloudSnow icon for snow nowcasts so the chip reads at a glance.
  const NowcastIcon = nowcast && /snow/i.test(nowcast) ? CloudSnow : Umbrella;

  return (
    <>
      <div className="px-5 pb-1 pt-3">
        <button
          type="button"
          onClick={() => setDetailOpen(true)}
          aria-label="Open weather details"
          className={`relative block w-full overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br ${gradient} text-left shadow-lg transition-transform active:scale-[0.98] ${textColor}`}
        >
          {/* Subtle backdrop icon — large and faint behind the temp. */}
          <div className="pointer-events-none absolute -right-4 -top-4 opacity-15">
            <IconFor category={current.category} className="h-40 w-40" />
          </div>

          {/* Content — packed: hero cluster up top, daypart band pinned to
              the bottom (mt-auto). Dropped justify-between + trimmed the
              height; it was spreading three blocks across a too-tall card and
              leaving big gaps between condition → temp → dayparts.
              2026-06-06 tighten. */}
          <div className="relative flex h-[160px] flex-col p-4">
            {/* Hero cluster: location + condition + H/L (left) beside the big
                temp (right), packed together so there's no gap between them. */}
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPickerOpen(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        setPickerOpen(true);
                      }
                    }}
                    className="inline-flex min-w-0 items-center gap-1 text-sm font-medium opacity-95 active:opacity-75"
                    aria-label="Change location"
                  >
                    <span className="truncate">{apiLocation.name}</span>
                    <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-80" />
                  </span>
                  {isFetching ? (
                    <RefreshCw className="h-3 w-3 shrink-0 animate-spin opacity-60" />
                  ) : null}
                </div>
                <div className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider opacity-80">
                  {current.condition_short}
                </div>
                <div className="mt-1.5 text-xs opacity-80">
                  H {hi} L {lo} · feels {current.feels_like_f}°
                </div>
              </div>
              <div className="shrink-0 text-6xl font-thin leading-none -tracking-tight">
                {current.temp_f}°F
              </div>
            </div>

            {/* Bottom band — pinned to the bottom edge (mt-auto). 4-daypart
                precip glance (current + upcoming) above the nowcast pill + tap
                hint. Compact Morn/Aft/Eve/Night precip-chance strip so the
                owner can plan washes without opening the modal. Derived from
                the detail payload's hourly data (peak hour per window;
                Overnight = the coming night). Self-hides until the detail
                payload is available. */}
            <div className="mt-auto flex flex-col gap-1.5">
              {cardDayparts.length > 0 ? (
                <div className="flex items-stretch gap-1">
                  {cardDayparts.map((dp) => (
                    <div
                      key={dp.key}
                      className={`flex flex-1 flex-col items-center gap-0.5 rounded-lg px-1 py-1 ${
                        dp.isCurrent ? 'bg-white/20 backdrop-blur-sm' : ''
                      }`}
                    >
                      <span className="text-[9px] font-semibold uppercase tracking-wide opacity-75">
                        {dp.short}
                      </span>
                      <span className="inline-flex items-center gap-0.5 text-xs font-semibold">
                        <Droplets className="h-3 w-3 opacity-80" />
                        {dp.peakPct !== null ? `${dp.peakPct}%` : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="flex items-center justify-between gap-2">
                {nowcast ? (
                  <span className="inline-flex items-center gap-1 truncate rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-medium backdrop-blur-sm">
                    <NowcastIcon className="h-3 w-3 shrink-0" />
                    <span className="truncate">{nowcast}</span>
                  </span>
                ) : (
                  <span />
                )}
                <span className="shrink-0 text-[10px] uppercase tracking-wider opacity-60">
                  Tap for details →
                </span>
              </div>
            </div>
          </div>
        </button>
      </div>
      <LocationPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
      />
      <WeatherDetailModal open={detailOpen} onClose={() => setDetailOpen(false)} />
    </>
  );
}
